// PowerSync database. Runs fully local when no backend is configured (demo
// mode) and connects to PowerSync + Supabase when env vars are present.
import { PowerSyncDatabase } from '@powersync/web';
import { AppSchema } from './schema';
import { env } from '../env';
import { SupabaseConnector } from './connector';
import { getSupabase } from '../supabase';

let dbPromise: Promise<PowerSyncDatabase> | null = null;

export function getDb(): Promise<PowerSyncDatabase> {
  dbPromise ??= (async () => {
    const db = new PowerSyncDatabase({
      schema: AppSchema,
      database: { dbFilename: 'klinixis.db', enableMultiTabs: typeof SharedWorker !== 'undefined' },
    });
    await db.init();
    if (import.meta.env.DEV) (window as unknown as { __klx_db?: unknown }).__klx_db = db; // debugging aid in dev only
    return db;
  })();
  return dbPromise;
}

let connected = false;

/** Start syncing. Safe to call repeatedly; only connects when a Supabase session exists. */
export async function connectIfSignedIn(db: PowerSyncDatabase): Promise<boolean> {
  if (!env.cloud || connected) return connected;
  const { data } = await getSupabase().auth.getSession();
  if (!data.session) return false;
  await db.connect(new SupabaseConnector());
  connected = true;
  return true;
}

/** Sign out: stop syncing and wipe local data (devices are shared in a clinic). */
export async function disconnectAndSignOut(db: PowerSyncDatabase): Promise<void> {
  if (env.cloud) {
    try { await getSupabase().auth.signOut(); } catch { /* offline: local sign-out still proceeds */ }
    await db.disconnectAndClear();
    connected = false;
  }
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function newId(): string {
  return crypto.randomUUID();
}

/** Resolve when every local change has been accepted by the server, or throw after timeoutMs. */
export async function waitForUploadsDrained(db: PowerSyncDatabase, timeoutMs = 30000): Promise<void> {
  const start = Date.now();
  for (;;) {
    const row = await db.getOptional<{ n: number }>('select count(*) as n from ps_crud');
    if (!row || row.n === 0) return;
    if (Date.now() - start > timeoutMs) throw new Error('Could not reach the server to finish setting up this device. Check the connection and sign in again.');
    await new Promise((r) => setTimeout(r, 500));
  }
}
